import { type ReactNode } from 'react';

// NGF-6268: the offers modules no longer self-provision their clickout props from provider
// context; whoever renders them (the page-template componentMap, or GridGroupOffers) must inject
// them. This guard makes a missing/malformed injection fail loudly at runtime instead of silently
// shipping broken clickout wiring. It is rendered inside the renderer's error boundary (RR7 atlas's
// OffersList ErrorBoundary, or atlas-astro's withIsland boundary), so throwing here hides only that
// offer widget rather than crashing the page.

type OffersClickoutValues = {
  [key: string]: unknown;
  clickoutService?: unknown;
  clickoutServiceUrl?: unknown;
  deviceType?: unknown;
  fullPageUrl?: unknown;
  gtmId?: unknown;
  pageUrl?: unknown;
};

const REQUIRED_NON_EMPTY_FIELDS = [
  'clickoutService',
  'clickoutServiceUrl',
  'deviceType',
  'fullPageUrl',
  'pageUrl',
] as const;

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const isMalformedUrl = (value: unknown): boolean =>
  typeof value === 'string' && (value.includes('undefined') || value.includes('null'));

/**
 * Validates that the clickout props required by the offers modules are present and well-formed.
 * Returns the list of problems found (empty when valid) so callers can decide how to react.
 */
export const getOffersClickoutProblems = (values: OffersClickoutValues): string[] => {
  const problems: string[] = [];

  for (const field of REQUIRED_NON_EMPTY_FIELDS) {
    if (!isNonEmptyString(values[field])) {
      problems.push(`missing or empty "${field}"`);
    }
  }

  // gtmId is analytics-only and is legitimately empty in some environments — the TEST config sets it
  // to '', and a repub can ship before GTM is configured. It must still be *injected* (a string) so
  // a renderer that forgets to wire the clickout props is caught, but an empty value must not hide a
  // revenue-generating offers widget. So require it to be present, not to be non-empty.
  if (typeof values.gtmId !== 'string') {
    problems.push('missing "gtmId"');
  }

  if (isMalformedUrl(values.clickoutServiceUrl)) {
    problems.push(`malformed "clickoutServiceUrl" (${String(values.clickoutServiceUrl)})`);
  }

  return problems;
};

/**
 * Throws (after logging) when the offers clickout props are missing/malformed. Must be called from
 * within an ErrorBoundary subtree so the failure hides the widget rather than crashing the page.
 */
export const assertOffersClickoutProps = (componentName: string, values: OffersClickoutValues): void => {
  const problems = getOffersClickoutProblems(values);

  if (problems.length === 0) {
    return;
  }

  const message = `[${componentName}] Offers clickout props are not wired up correctly: ${problems.join(
    ', ',
  )}. These props must be injected by the renderer (page-template componentMap or GridGroupOffers).`;

  // eslint-disable-next-line no-console
  console.error(message, values);

  throw new Error(message);
};

export const OffersClickoutGuard = ({
  children,
  componentName,
  values,
}: {
  children: ReactNode;
  componentName: string;
  values: OffersClickoutValues;
}): ReactNode => {
  assertOffersClickoutProps(componentName, values);

  return children;
};
